import styles from './Skeleton.module.css'

type SkeletonProps = {
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded'
  width?: string | number
  height?: string | number
  className?: string
  animation?: 'pulse' | 'wave' | 'none'
}

/**
 * Skeleton loading placeholder component
 * Use this to show loading states while data is being fetched
 */
export function Skeleton({
  variant = 'text',
  width,
  height,
  className = '',
  animation = 'pulse',
}: SkeletonProps) {
  const style: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  }

  return (
    <div
      className={`${styles.skeleton} ${styles[variant]} ${styles[animation]} ${className}`}
      style={style}
      aria-hidden="true"
    />
  )
}

/**
 * Skeleton for table rows - shows multiple columns
 */
export function TableRowSkeleton({ columns = 5 }: { columns?: number }) {
  return (
    <tr className={styles.tableRow}>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className={styles.tableCell}>
          <Skeleton variant="text" height={16} />
        </td>
      ))}
    </tr>
  )
}

/**
 * Skeleton for a card with title and content
 */
export function CardSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className={styles.card}>
      <Skeleton variant="text" width="60%" height={20} />
      <div className={styles.cardContent}>
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton
            key={i}
            variant="text"
            width={i === lines - 1 ? '80%' : '100%'}
            height={14}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Skeleton for stat cards (like dashboard metrics)
 */
export function StatCardSkeleton() {
  return (
    <div className={styles.statCard}>
      <Skeleton variant="text" width="40%" height={12} />
      <Skeleton variant="text" width="60%" height={28} />
      <Skeleton variant="text" width="50%" height={12} />
    </div>
  )
}

/**
 * Skeleton for list items with avatar
 */
export function ListItemSkeleton({ showAvatar = true }: { showAvatar?: boolean }) {
  return (
    <div className={styles.listItem}>
      {showAvatar && <Skeleton variant="circular" width={40} height={40} />}
      <div className={styles.listItemContent}>
        <Skeleton variant="text" width="70%" height={16} />
        <Skeleton variant="text" width="50%" height={12} />
      </div>
    </div>
  )
}

/**
 * Full page skeleton for dashboard
 */
export function DashboardSkeleton() {
  return (
    <div className={styles.dashboardSkeleton}>
      {/* Stats row */}
      <div className={styles.statsRow}>
        {Array.from({ length: 4 }).map((_, i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>

      {/* Main content area */}
      <div className={styles.mainContent}>
        <div className={styles.mainColumn}>
          <CardSkeleton lines={5} />
          <CardSkeleton lines={4} />
        </div>
        <div className={styles.sideColumn}>
          <CardSkeleton lines={3} />
          <CardSkeleton lines={3} />
        </div>
      </div>
    </div>
  )
}

/**
 * Skeleton for data tables
 */
export function TableSkeleton({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className={styles.tableSkeleton}>
      {/* Header */}
      <div className={styles.tableHeader}>
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} variant="text" width={80} height={14} />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={styles.tableRowSkeleton}>
          {Array.from({ length: columns }).map((_, j) => (
            <Skeleton
              key={j}
              variant="text"
              width={j === 0 ? '70%' : '50%'}
              height={14}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Skeleton for forms
 */
export function FormSkeleton({ fields = 4 }: { fields?: number }) {
  return (
    <div className={styles.formSkeleton}>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className={styles.formField}>
          <Skeleton variant="text" width={100} height={12} />
          <Skeleton variant="rounded" width="100%" height={40} />
        </div>
      ))}
      <div className={styles.formActions}>
        <Skeleton variant="rounded" width={100} height={40} />
        <Skeleton variant="rounded" width={100} height={40} />
      </div>
    </div>
  )
}

/**
 * Skeleton for the client detail page header + tabs + content
 */
export function ClientDetailSkeleton() {
  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header: avatar + name + meta */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-5)', marginBottom: 'var(--space-6)' }}>
        <Skeleton variant="circular" width={72} height={72} />
        <div style={{ flex: 1 }}>
          <Skeleton variant="text" width="40%" height={28} />
          <div style={{ marginTop: 8, display: 'flex', gap: 12 }}>
            <Skeleton variant="text" width={80} height={14} />
            <Skeleton variant="text" width={80} height={14} />
            <Skeleton variant="text" width={80} height={14} />
          </div>
        </div>
        <Skeleton variant="rounded" width={100} height={36} />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-6)', borderBottom: '1px solid var(--border-color)', paddingBottom: 'var(--space-3)' }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" width={90} height={32} />
        ))}
      </div>

      {/* Content: two columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-5)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <CardSkeleton lines={4} />
          <CardSkeleton lines={5} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <CardSkeleton lines={3} />
          <CardSkeleton lines={3} />
        </div>
      </div>
    </div>
  )
}

export default Skeleton
